import { supabase } from './supabase';

type EdgeFunctionErrorBody = {
  error?: string;
  message?: string;
  code?: string;
};

export class EdgeFunctionError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'EdgeFunctionError';
    this.code = code;
  }
}

type ParsedEdgeFunctionError = {
  message: string;
  code?: string;
};

/**
 * Surface `{ error }` or `{ code, message }` from a failed functions.invoke.
 */
export async function parseEdgeFunctionInvokeError(
  error: unknown,
  data: unknown,
): Promise<ParsedEdgeFunctionError> {
  let message: string | null = null;
  let code: string | undefined;
  const ctx = (error as { context?: unknown })?.context;
  if (ctx && typeof (ctx as Response).json === 'function') {
    try {
      const parsed = (await (ctx as Response).clone().json()) as EdgeFunctionErrorBody;
      code = parsed?.code;
      message = parsed?.error ?? parsed?.message ?? null;
    } catch {
      try {
        const text = await (ctx as Response).clone().text();
        if (text) {
          try {
            const parsed = JSON.parse(text) as EdgeFunctionErrorBody;
            code = parsed.code;
            message = parsed.error ?? parsed.message ?? text;
          } catch {
            message = text;
          }
        }
      } catch {
        /* ignore */
      }
    }
  }
  if (!message) {
    const fromData = data as EdgeFunctionErrorBody | null;
    code = code ?? fromData?.code;
    message = fromData?.error ?? fromData?.message ?? null;
  }
  return {
    message: message || (error instanceof Error ? error.message : 'Request failed'),
    code,
  };
}

/**
 * Resolve a live Engine user JWT for admin edge functions.
 * getSession() alone can return a revoked/expired session from localStorage.
 */
async function resolveEngineAccessToken(): Promise<string> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (user && !userError) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token?.trim()) return session.access_token;
  }

  const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
  const token = refreshed.session?.access_token?.trim();
  if (token && !refreshError) return token;

  throw new EdgeFunctionError(
    'Session expired — sign out and sign in again.',
    'SESSION_EXPIRED',
  );
}

/**
 * Invoke a Supabase edge function with an explicit Authorization header.
 * Avoids gateway 401s (UNAUTHORIZED_NO_AUTH_HEADER) when the client session
 * has not yet been attached to functions.invoke.
 */
export async function invokeEdgeFunction<T>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const token = await resolveEngineAccessToken();

  const { data, error } = await supabase.functions.invoke(name, {
    body,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (error) {
    const parsed = await parseEdgeFunctionInvokeError(error, data);
    throw new EdgeFunctionError(parsed.message, parsed.code);
  }

  return data as T;
}
